import { useState, useEffect, useCallback } from "react";
import {
  useUpdateTrackerTags,
  useTrackers,
} from "@/api/tracker";
import useNotif from "@/hooks/use-notif";

/**
 * Hook personnalisé pour gérer les tags d'un tracker
 * @param trackerId - L'ID du tracker
 * @returns Les tags du tracker (string[]), les fonctions pour ajouter/retirer des tags, et l'état de chargement
 */
export const useTrackerTags = (trackerId: string | null) => {
  const { addError, addSuccess } = useNotif();
  const [trackerTags, setTrackerTags] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Récupérer les trackers pour extraire les tags du tracker spécifique
  const { data: trackers, refetch: refetchTrackers } = useTrackers();

  const updateTrackerTagsMutation = useUpdateTrackerTags();

  // Extraire les tags du tracker depuis la liste des trackers
  useEffect(() => {
    if (trackerId && trackers) {
      const tracker = trackers.find((t) => t.id === trackerId);
      if (tracker && tracker.tags) {
        setTrackerTags(tracker.tags);
      } else {
        setTrackerTags([]);
      }
    } else {
      setTrackerTags([]);
    }
  }, [trackerId, trackers]);

  // Fonction pour rafraîchir les tags (rafraîchit la liste des trackers)
  const refetchTags = useCallback(async () => {
    await refetchTrackers();
  }, [refetchTrackers]);

  // Mettre à jour tous les tags du tracker en une seule requête
  const updateTags = useCallback(
    async (tagNames: string[]) => {
      if (!trackerId) return;

      setIsLoading(true);
      try {
        await updateTrackerTagsMutation.mutateAsync({
          uriParams: { id: trackerId },
          tags: tagNames,
        });
        addSuccess("Tags mis à jour avec succès");
        await refetchTags();
      } catch (error) {
        addError("Erreur lors de la mise à jour des tags");
      } finally {
        setIsLoading(false);
      }
    },
    [trackerId, updateTrackerTagsMutation, refetchTags, addSuccess, addError]
  );

  // Ajouter un tag au tracker
  const addTag = useCallback(
    async (tagName: string) => {
      if (!trackerId) return;

      // Vérifier si le tag est déjà associé
      if (trackerTags.includes(tagName)) {
        return; // Le tag est déjà associé
      }

      // Ajouter le nouveau tag à la liste existante
      const newTagNames = [...trackerTags, tagName];
      await updateTags(newTagNames);
    },
    [trackerId, trackerTags, updateTags]
  );

  // Retirer un tag du tracker
  const removeTag = useCallback(
    async (tagName: string) => {
      if (!trackerId) return;

      // Retirer le tag de la liste existante
      const newTagNames = trackerTags.filter((name) => name !== tagName);
      await updateTags(newTagNames);
    },
    [trackerId, trackerTags, updateTags]
  );

  // Toggle un tag (ajouter s'il n'est pas présent, retirer s'il est présent)
  const toggleTag = useCallback(
    async (tagName: string) => {
      if (!trackerId) return;

      const isTagPresent = trackerTags.includes(tagName);
      if (isTagPresent) {
        await removeTag(tagName);
      } else {
        await addTag(tagName);
      }
    },
    [trackerId, trackerTags, addTag, removeTag]
  );

  return {
    tags: trackerTags,
    isLoading: isLoading || (trackers === undefined),
    addTag,
    removeTag,
    toggleTag,
    updateTags,
    refetch: refetchTags,
  };
};

/**
 * Hook pour stocker et gérer les tags de plusieurs trackers
 * Utilise un état global pour stocker les tags au fur et à mesure qu'ils sont chargés
 * @param trackerIds - Les IDs des trackers (optionnel, pour initialisation)
 * @returns Un objet avec les tags de chaque tracker et des fonctions pour les mettre à jour
 */
export const useMultipleTrackerTags = (trackerIds: string[] = []) => {
  const [trackersTagsMap, setTrackersTagsMap] = useState<
    Record<string, string[]>
  >({});

  // Fonction pour mettre à jour les tags d'un tracker
  const updateTrackerTags = useCallback(
    (trackerId: string, tags: string[]) => {
      setTrackersTagsMap((prev) => ({
        ...prev,
        [trackerId]: tags,
      }));
    },
    []
  );

  return {
    trackersTagsMap,
    updateTrackerTags,
  };
};

